import type { HelpTopicPair } from "../types";

const topic: HelpTopicPair = {
  th: {
    id: "log-retention",
    title: "ระยะเวลาเก็บบันทึก",
    summary: "กำหนดว่าจะเก็บบันทึกการใช้งาน (audit logs) ไว้กี่วัน รายการที่เก่ากว่านั้นจะถูกลบอัตโนมัติ",
    access:
      "เปิดดูได้เมื่อมีสิทธิ์ settings:read (owner, admin, auditor) การเปลี่ยนค่าต้องมีสิทธิ์ settings:write (owner, admin) ผู้ที่ไม่มีสิทธิ์จะเห็นช่องเป็นแบบอ่านอย่างเดียว",
    shot: { file: "log-retention-page.png", caption: "หน้า การตั้งค่า → ระยะเวลาเก็บบันทึก" },
    sections: [
      {
        id: "setting",
        heading: "ช่อง “เก็บบันทึกการใช้งาน (วัน)”",
        fields: [
          {
            name: "เก็บบันทึกการใช้งาน (วัน)",
            required: true,
            description:
              "จำนวนวันที่จะเก็บแต่ละรายการนับจากวันที่เกิดเหตุการณ์ เป็นจำนวนเต็มตั้งแต่ 1 ถึง 3650 (ประมาณ 10 ปี) ค่าเริ่มต้นคือ 365 วัน ถ้าค่าอยู่นอกช่วง ปุ่ม “บันทึก” จะกดไม่ได้",
          },
        ],
        body: [
          "รายการที่เก่ากว่านี้จะถูกลบอัตโนมัติทุกไม่กี่ชั่วโมง (ระบบตรวจทุกประมาณ 6 ชั่วโมง) จึงอาจเห็นรายการที่เพิ่งเกินกำหนดค้างอยู่ได้ชั่วครู่",
        ],
      },
      {
        id: "effects",
        heading: "ผลของการเปลี่ยนค่า",
        body: [
          "ลดจำนวนวัน: ในรอบการลบครั้งถัดไป รายการทั้งหมดที่เก่ากว่าค่าใหม่จะถูกลบถาวร เช่น เปลี่ยนจาก 365 เป็น 90 วัน ประวัติที่เก่ากว่า 90 วันจะหายไปภายในไม่กี่ชั่วโมง",
          "เพิ่มจำนวนวัน: มีผลกับรายการที่ยังไม่ถูกลบเท่านั้น รายการที่ถูกลบไปแล้วจะไม่กลับมา",
          "ยิ่งเก็บนาน ฐานข้อมูลยิ่งใหญ่ขึ้น โดยเฉพาะระบบที่มีผู้ใช้เข้าสู่ระบบจำนวนมาก (ทุกการเข้าสู่ระบบสร้างหนึ่งรายการ)",
        ],
        steps: [
          "ตรวจนโยบายขององค์กรหรือข้อกำหนดทางกฎหมายว่าต้องเก็บหลักฐานการใช้งานอย่างน้อยกี่วัน",
          "กรอกจำนวนวันในช่อง",
          "กด “บันทึก”",
        ],
        warnings: [
          "การลดจำนวนวันทำให้ประวัติเก่าถูกลบถาวรและกู้คืนไม่ได้ ถ้าต้องเก็บไว้ ให้ส่งออกบันทึกการใช้งาน (CSV) หรือส่งออกข้อมูลแบบ “รวมบันทึกการใช้งาน” ก่อนลดค่า",
          "ถ้ามีผู้ดูแลคนอื่นบันทึกค่านี้ก่อนคุณ จะเห็นข้อความ “การตั้งค่าถูกแก้ไขโดยผู้อื่นแล้ว — โหลดใหม่เพื่อดูเวอร์ชันล่าสุด” ให้โหลดใหม่ก่อนแก้ไข",
        ],
        tips: ["การเปลี่ยนค่านี้ถูกบันทึกในบันทึกการใช้งานเป็น settings.updated (key: log-retention)"],
      },
    ],
    related: ["audit-logs", "data"],
  },
  en: {
    id: "log-retention",
    title: "Log retention",
    summary: "Sets how many days audit-log entries are kept. Older entries are deleted automatically.",
    access:
      "Viewing needs settings:read (owner, admin, auditor). Changing it needs settings:write (owner, admin); without it the field is read-only.",
    shot: { file: "log-retention-page.png", caption: "Settings → Log retention" },
    sections: [
      {
        id: "setting",
        heading: "The “Keep audit logs for (days)” field",
        fields: [
          {
            name: "Keep audit logs for (days)",
            required: true,
            description:
              "How many days each entry is kept, counted from when the event happened. A whole number from 1 to 3650 (about 10 years); the default is 365. Outside that range “Save” is disabled.",
          },
        ],
        body: [
          "Older entries are purged automatically every few hours (the platform checks about every 6 hours), so entries that have just expired may remain visible for a short while.",
        ],
      },
      {
        id: "effects",
        heading: "What a change does",
        body: [
          "Fewer days: at the next purge every entry older than the new value is deleted permanently. Going from 365 to 90 days removes all history older than 90 days within a few hours.",
          "More days: applies only to entries not yet deleted; entries already purged do not come back.",
          "Longer retention means a larger database, especially with many sign-ins (every sign-in creates one entry).",
        ],
        steps: [
          "Check your organization's policy or legal requirements for how long usage records must be kept.",
          "Enter the number of days.",
          "Click “Save”.",
        ],
        warnings: [
          "Reducing the number of days deletes old history permanently. If you need it, export the audit log (CSV) or run a data export with “Include audit logs” first.",
          "If another administrator saved this setting before you, you see “Settings were changed by someone else — reload to see the latest version.” Reload before editing.",
        ],
        tips: ["Changes are recorded in the audit log as settings.updated (key: log-retention)."],
      },
    ],
    related: ["audit-logs", "data"],
  },
};

export default topic;
